import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Alert, Button, Skeleton, Tabs, message } from 'antd';
import { CheckOutlined, FileExcelOutlined, SettingOutlined } from '@ant-design/icons';
import { ContentBackground, ContentBody, ContentHeader, ContentWrapper } from '@/components/content';
import { withPrivateRoute } from '@/components/hoc/withPrivateRoute';
import { ContainerFluid, Section } from '@/components/layouts';
import RootLayout from '@/components/layouts/RootLayout';
import { ROLES_PERMISSIONS } from '@/constants';
import { buildBoard } from '@/components/team-assignments/model';
import { confirmMyTeamCompanies, exportTeamBoard, getTeamBoard, getTeamTasks } from '@/services/teamAssignments.service';
import { getBillingOptions } from '@/services/teamBilling.service';
import { QueryParamsProvider } from '@/contexts/query-params/QueryParamsProvider';
import { useAuthStore } from '@/store/useAuthStore';
import { hasRequiredRoutesRoles } from '@/utils/authUtils';
import BillingView from './_components/BillingView';
import ByFunctionView from './_components/ByFunctionView';
import ClientContactsView from './_components/ClientContactsView';
import ContactCampaignView from './_components/ContactCampaignView';
import CombinedView from './_components/CombinedView';
import GeneralTasks, { TEAM_TASKS_KEY } from './_components/GeneralTasks';
import SettingsModal from './_components/SettingsModal';

const saveBlob = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * HLB team assignments: who from the team works on which client and in which
 * function. Managers (superAdmin, techAdmin, people named in the settings)
 * see and change every client; a staff member sees their own clients, keeps
 * the clients' contacts current and confirms the list.
 */
function TeamAssignmentsContent() {
  const { t } = useTranslation(['teamAssignments', 'menus', 'teamContacts', 'teamBilling']);
  const queryClient = useQueryClient();
  // Everything picked on the board lives in the address, so a link (from a
  // profile, or sent by Bora) opens the same view and a reload keeps it.
  const [params, setParams] = useSearchParams();
  // Touching any filter leaves the single-firm view (?company=) opened from a company profile.
  const setParam = (key, value, { keepFocus = false } = {}) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        if (value) n.set(key, value);
        else n.delete(key);
        if (key !== 'company' && !keepFocus) n.delete('company');
        return n;
      },
      { replace: true }
    );
  const view = ['byFunction', 'contacts', 'campaign', 'billing'].includes(params.get('view')) ? params.get('view') : 'combined';
  const setView = (v) => setParam('view', v === 'combined' ? '' : v);
  const fn = params.get('fn') || '';
  const setFn = (v) => setParam('fn', v);
  const person = params.get('person') || '';
  const setPerson = (v) => setParam('person', v || (data && !data.actor?.isManager ? 'all' : ''));
  // "companies": the list of all clients instead of the team overview.
  const list = params.get('list') || '';
  const setList = (v) => setParam('list', v);
  // Back to the team overview: one person, the client list and its filters all go.
  const goOverview = () =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        ['person', 'list', 'q', 'quick', 'company'].forEach((k) => n.delete(k));
        return n;
      },
      { replace: true }
    );
  const quick = params.get('quick') || 'all';
  const setQuick = (v) => setParam('quick', v === 'all' ? '' : v);
  const q = params.get('q') || '';
  // Searching is on the client list, also once the box is emptied: only "All employees" leaves it.
  const setQ = (v) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        if (v) n.set('q', v);
        else n.delete('q');
        n.set('list', 'companies');
        n.delete('company');
        return n;
      },
      { replace: true }
    );
  const focusCompany = params.get('company') || '';
  // From the contacts table to one company's card on the board.
  const openCompany = (companyId) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        n.delete('view');
        n.set('company', companyId);
        return n;
      },
      { replace: true }
    );
  const [clientStatus, setClientStatus] = useState(['ACTIVE']);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['team-board', clientStatus],
    queryFn: () => getTeamBoard({ clientStatus }),
  });
  const model = useMemo(() => buildBoard(data), [data]);
  // The server answers 403 to whoever does not get the billing tab.
  const { data: billingOptions } = useQuery({
    queryKey: ['team-billing-options'],
    queryFn: getBillingOptions,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const isManager = !!data?.actor?.isManager;
  const me = data?.actor?.employeeId;

  // A staff member starts on their own clients ('all' = they cleared it on purpose).
  useEffect(() => {
    if (data && !isManager && me && !params.get('person')) setParam('person', me, { keepFocus: true });
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps
  const personId = person === 'all' ? '' : person;

  const exporting = useMutation({
    mutationFn: () => exportTeamBoard({ clientStatus }),
    onSuccess: (blob) => saveBlob(blob, `HLB_tim_${new Date().toISOString().slice(0, 10)}.xlsx`),
    onError: (e) => message.error(e?.response?.data?.message || e.message),
  });

  const myGroups = me ? model.groups.filter((g) => g.people.has(me)) : [];
  // Covering for an absent colleague is not one of "my clients" to confirm.
  const isMine = (a) => a.employee === me && !a.substitution;
  const myPending = myGroups.filter((g) => g.rows.some((a) => isMine(a) && a.confirmation !== 'CONFIRMED'));
  const confirmAll = useMutation({
    mutationFn: () =>
      confirmMyTeamCompanies({
        companyIds: myPending.flatMap((g) => g.rows.filter(isMine).map((a) => a.company)),
        status: 'CONFIRMED',
      }),
    onSuccess: () => {
      message.success(t('messages.saved'));
      queryClient.invalidateQueries({ queryKey: ['team-board'] });
      queryClient.invalidateQueries({ queryKey: ['team-card'] });
    },
    onError: (e) => message.error(e?.response?.data?.message || e.message),
  });

  const title = isManager || !data ? t('teamAssignments', { ns: 'menus' }) : t('titleMine');

  return (
    <RootLayout metadata={{ title }}>
      <ContentBackground source="/assets/images/bg-pattern.svg" />
      <Section>
        <ContainerFluid className="foreground-wrapper">
          <ContentWrapper>
            {/* Export and settings sit in the header, so the board starts at the top. */}
            <ContentHeader title={title}>
              {/* The contacts and billing tabs have their own export in their table's header. */}
              {isManager && view !== 'contacts' && view !== 'billing' && (
                <Button shape="round" icon={<FileExcelOutlined />} loading={exporting.isPending} onClick={() => exporting.mutate()}>
                  {t('actions.export')}
                </Button>
              )}
              {isManager && (
                <Button shape="round" icon={<SettingOutlined />} onClick={() => setSettingsOpen(true)}>
                  {t('settings.button')}
                </Button>
              )}
            </ContentHeader>
            <ContentBody>
              {/* The board scrolls inside its own table, so the frame never scrolls sideways. */}
              <div className="with-y-overflow overflow-x-hidden">
                {/* minmax(0,1fr): the column may shrink below the table's width, so wide tables scroll inside themselves. */}
                <div className="grid grid-cols-[minmax(0,1fr)] gap-4 p-4">
                  {isLoading && <Skeleton active paragraph={{ rows: 10 }} />}
                  {error && <Alert type="error" showIcon message={error?.response?.data?.message || error.message} />}

                  {data && (
                    <>
                      {!isManager && !me && <Alert type="warning" showIcon message={t('notTeamMember')} />}
                      {!isManager && me && myGroups.length > 0 && (
                        <Alert
                          type={myPending.length ? 'info' : 'success'}
                          showIcon
                          message={t('confirm.progress', { done: myGroups.length - myPending.length, total: myGroups.length })}
                          description={myPending.length ? t('confirm.explain') : null}
                          action={
                            myPending.length ? (
                              <Button shape="round" type="primary" icon={<CheckOutlined />} loading={confirmAll.isPending} onClick={() => confirmAll.mutate()}>
                                {t('confirm.all')}
                              </Button>
                            ) : null
                          }
                        />
                      )}

                      <Tabs
                        activeKey={view}
                        onChange={setView}
                        items={[
                          {
                            key: 'combined',
                            label: t('tabs.combined'),
                            children: (
                              <CombinedView
                                model={model}
                                isManager={isManager}
                                me={me}
                                fn={fn}
                                setFn={setFn}
                                person={personId}
                                setPerson={setPerson}
                                list={list}
                                setList={setList}
                                goOverview={goOverview}
                                clientStatus={clientStatus}
                                setClientStatus={setClientStatus}
                                quick={quick}
                                setQuick={setQuick}
                                q={q}
                                setQ={setQ}
                                focusCompany={focusCompany}
                                onFocused={() => setParam('company', '')}
                              />
                            ),
                          },
                          {
                            key: 'byFunction',
                            label: t('tabs.byFunction'),
                            children: <ByFunctionView model={model} fn={fn} setFn={setFn} />,
                          },
                          {
                            key: 'contacts',
                            label: t('tab', { ns: 'teamContacts' }),
                            children: (
                              <QueryParamsProvider>
                                <ClientContactsView people={data.people} seesEveryone={!!isManager} onOpenCompany={openCompany} />
                              </QueryParamsProvider>
                            ),
                          },
                          ...(isManager
                            ? [
                                {
                                  key: 'campaign',
                                  label: t('campaign.tab', { ns: 'teamContacts' }),
                                  children: (
                                    <QueryParamsProvider>
                                      <ContactCampaignView />
                                    </QueryParamsProvider>
                                  ),
                                },
                              ]
                            : []),
                          ...(billingOptions
                            ? [
                                {
                                  key: 'billing',
                                  label: t('tab', { ns: 'teamBilling' }),
                                  children: (
                                    <QueryParamsProvider>
                                      <BillingView options={billingOptions} />
                                    </QueryParamsProvider>
                                  ),
                                },
                              ]
                            : []),
                        ]}
                      />
                    </>
                  )}
                </div>
              </div>
            </ContentBody>
          </ContentWrapper>
        </ContainerFluid>
      </Section>
      {isManager && <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />}
    </RootLayout>
  );
}

/**
 * What someone with `employee` as their only team role gets here: the general
 * tasks they lead or help on, to tick off. They have no board, so no team list,
 * clients or settings; the server answers with their tasks alone.
 */
function MyTasksContent() {
  const { t } = useTranslation('teamAssignments');
  // A refusal is an answer, so no retries. The list mounts only with the data: its
  // own query, on the same key, would otherwise retry and hide the refusal.
  const { data, error, isLoading } = useQuery({ queryKey: [TEAM_TASKS_KEY], queryFn: getTeamTasks, retry: false });
  const title = t('titleMyTasks');

  return (
    <RootLayout metadata={{ title }}>
      <ContentBackground source="/assets/images/bg-pattern.svg" />
      <Section>
        <ContainerFluid className="foreground-wrapper">
          <ContentWrapper>
            <ContentHeader title={title} />
            <ContentBody>
              <div className="with-y-overflow overflow-x-hidden">
                <div className="grid grid-cols-[minmax(0,1fr)] gap-4 p-4">
                  {isLoading && <Skeleton active paragraph={{ rows: 4 }} />}
                  {error && <Alert type="error" showIcon message={error?.response?.data?.message || error.message} />}
                  {data && <GeneralTasks people={[]} companies={[]} isManager={false} />}
                </div>
              </div>
            </ContentBody>
          </ContentWrapper>
        </ContainerFluid>
      </Section>
    </RootLayout>
  );
}

function TeamAssignmentsEntry() {
  const roles = useAuthStore((state) => state.user?.roles) || [];
  return hasRequiredRoutesRoles(roles, ROLES_PERMISSIONS.TEAM_ASSIGNMENTS.BOARD.requiredRoles) ? (
    <TeamAssignmentsContent />
  ) : (
    <MyTasksContent />
  );
}

const TeamAssignmentsPage = withPrivateRoute(TeamAssignmentsEntry, ROLES_PERMISSIONS.TEAM_ASSIGNMENTS.VIEW);
TeamAssignmentsPage.displayName = 'TeamAssignmentsPage';

export default TeamAssignmentsPage;
